import { Button, Divider, notification, Radio } from 'antd';
import React, { useState } from 'react';

import { TableCellDescriptions } from '../../../../components/TableCellDescriptions';
import { apiClient } from '../../../../graphql/api/apiClient';
import { EmergencyPlan, EmergencyScenarioTableFragment } from '../../../../graphql/api/generated';
import {
  deleteEmergencyScenarioAccessPoints,
  upsertEmergencyScenarioAccessPoints,
} from '../../../../graphql/api/operations';
import { displayErrorMessage } from '../../../../utils';
import {
  EmergencyPlanWithIcon,
} from '../../../EmergencyScenarioAccessPoint/components/EmergencyPlanWithIcon';

interface IEmergencyScenarioAccessPointsFormProps {
  emergencyScenario: EmergencyScenarioTableFragment;
  selectedRowKeys: string[];
  setSearch: (searchTerm: string) => void;
  setSelectedRowKeys: (keys: string[]) => void;
}

export const EmergencyScenarioAccessPointsForm: React.FC<
  IEmergencyScenarioAccessPointsFormProps
> = (props) => {
  const { emergencyScenario, selectedRowKeys, setSearch, setSelectedRowKeys } = props;
  const { emergencyScenarioId } = emergencyScenario;
  const [selectedEmergencyPlan, setSelectedEmergencyPlan] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const cursorStyle = selectedRowKeys.length ? 'pointer' : 'disabled';

  return (
    <>
      <TableCellDescriptions
        descriptions={{
          'Total Access Points Selected': (
            <div style={{ paddingBottom: 20 }}>{selectedRowKeys.length}</div>
          ),
          'Select Emergency Plan': (
            <Radio.Group
              value={selectedEmergencyPlan}
              onChange={(e) => setSelectedEmergencyPlan(e.target.value)}
              style={{ display: 'block', marginBottom: 20 }}
              disabled={!selectedRowKeys.length}
            >
              {[
                {
                  value: EmergencyPlan.LOCKDOWN,
                  label: (
                    <EmergencyPlanWithIcon
                      emergencyPlan={EmergencyPlan.LOCKDOWN}
                      style={{ cursor: cursorStyle }}
                    />
                  ),
                },
                {
                  value: EmergencyPlan.UNLOCKED,
                  label: (
                    <EmergencyPlanWithIcon
                      emergencyPlan={EmergencyPlan.UNLOCKED}
                      style={{ cursor: cursorStyle }}
                    />
                  ),
                },
                {
                  value: 'NONE',
                  label: 'None',
                },
              ].map(({ value, label }) => (
                <Radio key={value} value={value}>{label}</Radio>
              ))}
            </Radio.Group>
          ),
        }}
      />
      <Divider />
      <section className='form-submit-section'>
        <Button
          htmlType='submit'
          type='primary'
          size='large'
          loading={submitting}
          disabled={!selectedRowKeys.length || !selectedEmergencyPlan || submitting}
          onClick={async () => {
            try {
              setSubmitting(true);

              if (selectedEmergencyPlan === 'NONE') {
                await deleteEmergencyScenarioAccessPoints(emergencyScenarioId, selectedRowKeys);
              } else {
                const inputs = selectedRowKeys.map((accessPointId) => ({
                  accessPointId,
                  emergencyPlan: selectedEmergencyPlan as EmergencyPlan,
                }));

                await upsertEmergencyScenarioAccessPoints(emergencyScenarioId, inputs);
              }

              setSearch('');
              setSelectedRowKeys([]);
              setSelectedEmergencyPlan(undefined);

              notification.success({
                message: 'Success',
                description: 'The access point emergency plans were successfully saved!',
              });

              await apiClient.resetStore();
            } catch (error) {
              displayErrorMessage(error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </section>
    </>
  );
};
